import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Image as ImageIcon, Download, Copy, CheckCircle2, Trash2, Instagram, RefreshCw } from "lucide-react";

export default function SocialStudio() {
  const [posts, setPosts] = useState(null);
  const [tab, setTab] = useState("draft");
  const [theme, setTheme] = useState("");
  const [count, setCount] = useState(3);
  const [generating, setGenerating] = useState(false);
  const [packs, setPacks] = useState({});
  const [audience, setAudience] = useState("hs_coaches");

  const load = () => {
    api.get("/admin/social").then(({ data }) => setPosts(data.posts)).catch(() => toast.error("Failed to load posts"));
  };
  useEffect(() => {
    load();
    api.get("/admin/social/hashtag-packs").then(({ data }) => setPacks(data.packs || {})).catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/social/generate", {
        count: Number(count), theme: theme || undefined, audience: audience || undefined,
      });
      toast.success(`${data.created} post drafts ready`);
      setTab("draft");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const pack = packs[audience];
  const copyPack = () => {
    navigator.clipboard.writeText((pack?.hashtags || []).map((h) => `#${h}`).join(" "));
    toast.success("Hashtag pack copied");
  };

  const shown = (posts || []).filter((p) => p.status === tab);

  return (
    <div className="text-white">
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-5xl" data-testid="social-studio-page">
        <Link to="/ad/marketing" className="text-xs text-neutral-400 hover:text-white flex items-center gap-1 mb-4" data-testid="back-to-growth">
          <ArrowLeft size={12} /> GROWTH CONSOLE
        </Link>
        <div className="text-xs tracking-widest text-volt flex items-center gap-2"><Instagram size={14} /> ADMIN · SOCIAL CONTENT STUDIO</div>
        <h1 className="heading text-4xl sm:text-5xl lg:text-6xl mt-2">INSTAGRAM STUDIO</h1>
        <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
          Claude drafts post copy from live platform stats; Nano Banana renders the square graphic. Review, edit, approve — then download the image and copy the caption to post. Nothing goes out automatically.
        </p>

        <div className="mt-6 border border-neutral-800 bg-appcard p-5" data-testid="social-generate-panel">
          <div className="text-xs tracking-widest text-volt">GENERATE NEW DRAFTS</div>
          <div className="mt-3">
            <span className="text-[10px] tracking-widest text-neutral-500">TARGET AUDIENCE (DRIVES HASHTAG PACK + MESSAGING)</span>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="social-audience-chips">
              {Object.entries(packs).map(([key, p]) => (
                <button key={key} onClick={() => setAudience(key)} data-testid={`social-audience-${key}`}
                  className={`px-3 min-h-[34px] text-[11px] tracking-widest border transition-colors ${audience === key ? "bg-blaze border-blaze text-white" : "border-neutral-700 text-neutral-300 hover:border-blaze"}`}>
                  {p.label.toUpperCase()}
                </button>
              ))}
            </div>
            {pack && (
              <div className="mt-3 border border-neutral-800 bg-appbg p-3" data-testid="social-pack-preview">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] tracking-widest text-neutral-500">TARGETED HASHTAGS — {pack.label.toUpperCase()}</span>
                  <button onClick={copyPack} data-testid="social-pack-copy"
                    className="text-[10px] tracking-widest text-volt hover:text-white transition-colors flex items-center gap-1">
                    <Copy size={10} /> COPY PACK
                  </button>
                </div>
                <div className="mt-1 text-xs text-volt break-words">{(pack.hashtags || []).map((h) => `#${h}`).join(" ")}</div>
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="block flex-1 min-w-[240px]">
              <span className="text-[10px] tracking-widest text-neutral-500">THEME / ANGLE (OPTIONAL)</span>
              <input value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="social-theme-input"
                placeholder="e.g., coach claim CTA, new HS openings in Ohio, CIR explainer…"
                className="mt-1 w-full bg-appbg border border-neutral-700 px-3 py-2 text-sm focus:border-volt outline-none" />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest text-neutral-500">POSTS</span>
              <select value={count} onChange={(e) => setCount(e.target.value)} data-testid="social-count-select"
                className="mt-1 bg-appbg border border-neutral-700 px-3 py-2 text-sm focus:border-volt outline-none">
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button onClick={generate} disabled={generating} data-testid="social-generate-btn"
              className="min-h-[42px] px-5 bg-blaze text-white heading text-sm tracking-widest hover:glow-blaze transition-all disabled:opacity-50 flex items-center gap-2">
              <Sparkles size={14} /> {generating ? "DRAFTING…" : "GENERATE POSTS"}
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-1 border-b border-neutral-800">
          {["draft", "approved"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`social-tab-${t}`}
              className={`px-4 py-2 text-xs tracking-widest border-b-2 transition-colors ${tab === t ? "border-blaze text-white" : "border-transparent text-neutral-400 hover:text-white"}`}>
              {t.toUpperCase()} ({(posts || []).filter((p) => p.status === t).length})
            </button>
          ))}
        </div>

        {!posts ? (
          <div className="p-10 heading text-2xl text-neutral-500">LOADING…</div>
        ) : shown.length === 0 ? (
          <div className="mt-6 border border-dashed border-neutral-800 p-10 text-neutral-500" data-testid="social-empty">
            {tab === "draft" ? "No drafts — generate some above." : "Nothing approved yet."}
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="social-post-list">
            {shown.map((p) => <PostCard key={p.id} post={p} onChanged={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function PostCard({ post, onChanged }) {
  const [caption, setCaption] = useState(post.caption || "");
  const [imagePrompt, setImagePrompt] = useState(post.image_prompt || "");
  const [busy, setBusy] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);

  useEffect(() => {
    if (post.has_image) loadImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.has_image, post.image_generated_at]);

  const loadImage = async () => {
    try {
      const token = localStorage.getItem("cc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/social/${post.id}/image`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setImgUrl(URL.createObjectURL(await res.blob()));
    } catch { /* no image yet */ }
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.patch(`/admin/social/${post.id}`, { caption, image_prompt: imagePrompt });
      toast.success("Post saved");
    } catch { toast.error("Save failed"); }
    setBusy(null);
  };

  const genImage = async () => {
    setBusy("image");
    try {
      await api.post(`/admin/social/${post.id}/image`);
      toast.success("Image rendered");
      await loadImage();
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Image generation failed"); }
    setBusy(null);
  };

  const approve = async () => {
    setBusy("approve");
    try {
      await api.post(`/admin/social/${post.id}/approve`);
      toast.success("Approved — ready to export");
      onChanged();
    } catch { toast.error("Approve failed"); }
    setBusy(null);
  };

  const remove = async () => {
    setBusy("delete");
    try {
      await api.delete(`/admin/social/${post.id}`);
      toast.success("Post deleted");
      onChanged();
    } catch { toast.error("Delete failed"); setBusy(null); }
  };

  const copyCaption = () => {
    const tags = (post.hashtags || []).map((h) => `#${h}`).join(" ");
    navigator.clipboard.writeText(`${caption}\n\n${tags}`);
    toast.success("Caption + hashtags copied — paste into Instagram");
  };

  const download = () => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `coachconnect-ig-${post.id.slice(0, 8)}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="border border-neutral-800 bg-appcard p-4" data-testid={`social-post-${post.id}`}>
      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-64 shrink-0">
          {imgUrl ? (
            <img src={imgUrl} alt="Post graphic" className="w-full aspect-square object-cover border border-neutral-700" data-testid={`social-image-${post.id}`} />
          ) : (
            <div className="w-full aspect-square border border-dashed border-neutral-700 flex flex-col items-center justify-center text-neutral-600 gap-2">
              <ImageIcon size={28} />
              <span className="text-[10px] tracking-widest">NO IMAGE YET</span>
            </div>
          )}
          <button onClick={genImage} disabled={!!busy} data-testid={`social-gen-image-${post.id}`}
            className="mt-2 w-full min-h-[38px] border border-volt text-volt text-[10px] tracking-widest hover:bg-volt hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
            {busy === "image" ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {busy === "image" ? "RENDERING… (~20s)" : imgUrl ? "RE-RENDER IMAGE" : "GENERATE IMAGE"}
          </button>
        </div>

        <div className="flex-1 min-w-0">
          {post.audience_label && (
            <div className="inline-block mb-1 px-2 py-0.5 border border-volt/50 text-volt text-[10px] tracking-widest" data-testid={`social-audience-badge-${post.id}`}>
              TARGET: {post.audience_label.toUpperCase()}
            </div>
          )}
          {post.hook && <div className="heading text-lg text-white">{post.hook}</div>}
          <label className="block mt-2">
            <span className="text-[10px] tracking-widest text-neutral-500">CAPTION</span>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={7} data-testid={`social-caption-${post.id}`}
              className="mt-1 w-full bg-appbg border border-neutral-700 px-3 py-2 text-sm leading-relaxed focus:border-blaze outline-none" />
          </label>
          <div className="mt-2 text-xs text-volt break-words" data-testid={`social-hashtags-${post.id}`}>
            {(post.hashtags || []).map((h) => `#${h}`).join(" ")}
          </div>
          <label className="block mt-2">
            <span className="text-[10px] tracking-widest text-neutral-500">IMAGE PROMPT</span>
            <textarea value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)} rows={3} data-testid={`social-image-prompt-${post.id}`}
              className="mt-1 w-full bg-appbg border border-neutral-700 px-3 py-2 text-xs text-neutral-400 font-mono focus:border-blaze outline-none" />
          </label>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button onClick={save} disabled={!!busy} data-testid={`social-save-${post.id}`}
              className="min-h-[38px] px-4 border border-neutral-700 text-neutral-300 text-xs tracking-widest hover:border-volt hover:text-volt transition-colors disabled:opacity-50">
              {busy === "save" ? "SAVING…" : "SAVE EDITS"}
            </button>
            <button onClick={copyCaption} data-testid={`social-copy-${post.id}`}
              className="min-h-[38px] px-4 border border-neutral-700 text-neutral-300 text-xs tracking-widest hover:border-volt hover:text-volt transition-colors flex items-center gap-1.5">
              <Copy size={12} /> COPY CAPTION
            </button>
            {imgUrl && (
              <button onClick={download} data-testid={`social-download-${post.id}`}
                className="min-h-[38px] px-4 border border-neutral-700 text-neutral-300 text-xs tracking-widest hover:border-volt hover:text-volt transition-colors flex items-center gap-1.5">
                <Download size={12} /> DOWNLOAD IMAGE
              </button>
            )}
            {post.status === "draft" && (
              <button onClick={approve} disabled={!!busy} data-testid={`social-approve-${post.id}`}
                className="min-h-[38px] px-4 bg-blaze text-white heading text-xs tracking-widest hover:glow-blaze transition-all disabled:opacity-50 flex items-center gap-1.5">
                <CheckCircle2 size={13} /> APPROVE
              </button>
            )}
            <button onClick={remove} disabled={!!busy} data-testid={`social-delete-${post.id}`}
              className="ml-auto min-h-[38px] px-3 border border-red-900/60 text-red-400 text-xs tracking-widest hover:border-red-500 transition-colors disabled:opacity-50 flex items-center gap-1.5">
              <Trash2 size={12} /> DELETE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
